/**
 * ZAXIA Frontend API Client
 * Typed fetch functions for backend API communication
 */

import { API_BASE } from '@/config/api';

// =============================================================================
// API TYPES (matching backend responses)
// =============================================================================

export interface ApiHotel {
  hotelId: string;
  name: string;
  timezone: string;
  currency: string;
  defaultLanguage: string;
  languages: string[];
  operatingHours: {
    spa: { open: string; close: string };
    restaurant: {
      breakfast: { open: string; close: string };
      lunch: { open: string; close: string };
      dinner: { open: string; close: string };
    };
  };
  policies: {
    lateCheckoutFee: number;
    lateCheckoutMaxHour: string;
    minibarPosting: boolean;
  };
}

export interface ApiGuest {
  guestId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  language: string;
  preferences: {
    spaInterest: boolean;
    diningInterest: boolean;
    sustainabilityOptIn: boolean;
  } | null;
}

export interface ApiRoom {
  roomId: string;
  hotelId: string;
  roomNumber: string;
  roomType: 'STANDARD' | 'DELUXE' | 'SUITE';
  floor: number;
  status: 'VACANT' | 'OCCUPIED' | 'CLEANING' | 'OUT_OF_SERVICE';
  tvId: string | null;
  currentStayId: string | null;
}

export interface ApiStay {
  stayId: string;
  hotelId: string;
  roomId: string;
  guestId: string;
  checkInDate: string;
  checkOutDate: string;
  actualCheckIn: string | null;
  actualCheckOut: string | null;
  status: 'RESERVED' | 'CHECKED_IN' | 'CHECKED_OUT' | 'CANCELLED';
  greenPreferences: {
    greenStayOptIn: boolean;
    skipCleaningDates: string[];
    towelsOnlyDates: string[];
  };
}

export interface ApiSession {
  stayId: string;
  roomNumber: string;
  onboardingCompleted: boolean;
  guest: ApiGuest;
  room: ApiRoom;
  stay: ApiStay;
}

export interface ApiService {
  serviceId: string;
  hotelId: string;
  category: string;
  name: string;
  description: string;
  durationMinutes: number;
  price: number | null;
  capacity: number;
  available: boolean;
}

export interface ApiBooking {
  bookingId: string;
  stayId: string;
  serviceId: string;
  serviceName: string;
  category: string;
  datetime: string;
  durationMinutes: number;
  status: 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
  price: number | null;
  location: string | null;
  notes: string | null;
  createdAt: string;
}

export interface ApiCalendarEvent {
  eventId: string;
  stayId: string;
  serviceType: string;
  title: string;
  datetime: string;
  durationMinutes: number;
  location: string | null;
  sourceBookingId: string | null;
  sourceTaskId: string | null;
  status: 'CONFIRMED' | 'CANCELLED';
}

export interface ApiBillCharge {
  chargeId: string;
  stayId: string;
  category: string;
  description: string;
  notes?: string;
  amount: number;
  datetime: string;
  bookingId?: string;
}

export interface ApiBill {
  stayId: string;
  guestName: string;
  roomNumber: string;
  checkInDate: string;
  checkOutDate: string;
  charges: ApiBillCharge[];
  totalAmount: number;
  currency: string;
}

export interface CreateBookingRequest {
  serviceId: string;
  serviceName: string;
  category: string;
  datetime: string;
  durationMinutes?: number;
  price?: number | null;
  location?: string;
  notes?: string; // Extra detail shown in bill and calendar event (e.g. ordered items)
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Fetch hotel configuration
 */
export async function fetchHotel(): Promise<ApiHotel> {
  const response = await fetch(`${API_BASE}/hotel`);
  if (!response.ok) {
    throw new Error(`Failed to fetch hotel: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get the room number from the URL ?room= parameter.
 * Simulates a room's TV/tablet identity for development/demo.
 * In production, this would be a device-level config.
 */
export function getRoomParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('room');
}

/**
 * Build query string with room param if present
 */
function withRoomParam(base: string, room?: string | null): string {
  const roomNumber = room ?? getRoomParam();
  if (roomNumber) {
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}room=${roomNumber}`;
  }
  return base;
}

/**
 * Fetch current session (guest, room, stay)
 * Pass ?room=XXX to get a specific room's session
 */
export async function fetchSession(room?: string | null): Promise<ApiSession> {
  const url = withRoomParam(`${API_BASE}/session`, room);
  const response = await fetch(url);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to fetch session: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Phase D4.2: Update green stay preferences
 */
export interface UpdateGreenPreferencesRequest {
  greenStayOptIn?: boolean;
  skipCleaningDates?: string[];
  towelsOnlyDates?: string[];
}

export interface UpdateGreenPreferencesResponse {
  greenPreferences: {
    greenStayOptIn: boolean;
    skipCleaningDates: string[];
    towelsOnlyDates: string[];
  };
}

export async function updateGreenPreferences(
  data: UpdateGreenPreferencesRequest
): Promise<UpdateGreenPreferencesResponse> {
  const url = withRoomParam(`${API_BASE}/session/green-preferences`);
  const response = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to update green preferences: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Phase P2.4-5: Mark onboarding as completed for current stay
 */
export interface CompleteOnboardingResponse {
  success: boolean;
  onboardingCompleted: boolean;
}

export async function completeOnboarding(): Promise<CompleteOnboardingResponse> {
  const url = withRoomParam(`${API_BASE}/onboarding/complete`);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to complete onboarding: ${response.statusText}`);
  }
  return response.json();
}

// =============================================================================
// STAFF BOOKINGS (Phase P2.5-2)
// =============================================================================

export interface StaffBooking {
  stayId: string;
  roomNumber: string;
  guestName: string;
  checkInDate: string;
  checkOutDate: string;
  status: 'UPCOMING' | 'ACTIVE' | 'COMPLETED';
  onboardingCompleted: boolean;
  greenStayActive: boolean;
  createdAt: string;
}

export interface StaffBookingsResponse {
  bookings: StaffBooking[];
  count: number;
}

export async function fetchStaffBookings(): Promise<StaffBookingsResponse> {
  const response = await fetch(`${API_BASE}/staff/bookings`);
  if (!response.ok) {
    throw new Error(`Failed to fetch bookings: ${response.statusText}`);
  }
  return response.json();
}

// =============================================================================
// SERVICES
// =============================================================================

/**
 * Fetch all services
 */
export async function fetchServices(): Promise<ApiService[]> {
  const response = await fetch(`${API_BASE}/services`);
  if (!response.ok) {
    throw new Error(`Failed to fetch services: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch bookings for current stay
 */
export async function fetchBookings(): Promise<ApiBooking[]> {
  const url = withRoomParam(`${API_BASE}/bookings`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch bookings: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create a new booking
 */
export async function createBooking(data: CreateBookingRequest): Promise<ApiBooking> {
  const url = withRoomParam(`${API_BASE}/bookings`);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to create booking: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Cancel a booking
 */
export async function cancelBooking(bookingId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/bookings/${bookingId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to cancel booking: ${response.statusText}`);
  }
}

/**
 * Phase D4.5.4: Update a booking (datetime, partySize)
 */
export interface UpdateBookingRequest {
  datetime?: string;
  partySize?: number;
}

export async function updateBooking(bookingId: string, data: UpdateBookingRequest): Promise<ApiBooking> {
  const response = await fetch(`${API_BASE}/bookings/${bookingId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(errorBody || `Failed to update booking: ${response.statusText}`);
  }
  return response.json();
}

// =============================================================================
// GUEST REQUESTS (Phase P2.2-3)
// =============================================================================

export enum GuestRequestCategory {
  HOUSEKEEPING = 'HOUSEKEEPING',
  CONCIERGE = 'CONCIERGE',
  MAINTENANCE = 'MAINTENANCE',
  OTHER = 'OTHER',
}

export enum GuestRequestStatus {
  NEW = 'NEW',
  /**
   * OPS-38 / OPS-38.1: ESCALATED is an attention-state, not a workflow-completion-state.
   * It does NOT imply ownership, dispatch, or assignment. Escalated items must
   * remain visible across every "open requests" surface and become MORE prominent.
   */
  ESCALATED = 'ESCALATED',
  ASSIGNED = 'ASSIGNED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

// =============================================================================
// OPS-38.2: Canonical Operational-State Taxonomy (frontend mirror)
// =============================================================================
// Mirror of backend/src/hotel-lab/types/index.ts. Both files MUST stay in
// lockstep — every helper here has a backend twin with identical extension.
// Use these helpers everywhere the frontend asks "is this request open /
// awaiting / in-flight?" instead of inline status comparisons.
//
// Architectural contract: docs/PHASE_OPS38_OPERATIONAL_SEMANTICS.md
//   This file is part of the protected surface defined in §13 of the
//   contract. Adding, removing, or reclassifying a status — or changing
//   the membership of any axis set / predicate below — requires
//   following the ten-step change protocol in that document. The
//   frontend mirror cannot drift from the backend canonical types; both
//   move together or neither moves.
// -----------------------------------------------------------------------------

/** Terminal states — request is closed and operationally invisible. */
export const RESOLVED_REQUEST_STATUSES: ReadonlySet<GuestRequestStatus> = new Set([
  GuestRequestStatus.COMPLETED,
]);

/** Queue side: no human owns the work yet (NEW or ESCALATED). */
export const AWAITING_DISPATCH_REQUEST_STATUSES: ReadonlySet<GuestRequestStatus> = new Set([
  GuestRequestStatus.NEW,
  GuestRequestStatus.ESCALATED,
]);

/** In-flight side: a human owns the work. */
export const ACTIVELY_WORKED_REQUEST_STATUSES: ReadonlySet<GuestRequestStatus> = new Set([
  GuestRequestStatus.ASSIGNED,
  GuestRequestStatus.IN_PROGRESS,
]);

/** Items requiring explicit supervisor attention. */
export const SUPERVISOR_ATTENTION_REQUEST_STATUSES: ReadonlySet<GuestRequestStatus> = new Set([
  GuestRequestStatus.ESCALATED,
]);

/** Anything not resolved — canonical replacement for the old "open" set. */
export const OPERATIONALLY_VISIBLE_REQUEST_STATUSES: ReadonlySet<GuestRequestStatus> = new Set([
  ...AWAITING_DISPATCH_REQUEST_STATUSES,
  ...ACTIVELY_WORKED_REQUEST_STATUSES,
]);

export function isRequestResolved(status: string | GuestRequestStatus): boolean {
  return RESOLVED_REQUEST_STATUSES.has(status as GuestRequestStatus);
}
export function isRequestAwaitingDispatch(status: string | GuestRequestStatus): boolean {
  return AWAITING_DISPATCH_REQUEST_STATUSES.has(status as GuestRequestStatus);
}
export function isRequestActivelyWorked(status: string | GuestRequestStatus): boolean {
  return ACTIVELY_WORKED_REQUEST_STATUSES.has(status as GuestRequestStatus);
}
export function isRequestSupervisorAttention(status: string | GuestRequestStatus): boolean {
  return SUPERVISOR_ATTENTION_REQUEST_STATUSES.has(status as GuestRequestStatus);
}
export function isRequestOperationallyVisible(status: string | GuestRequestStatus): boolean {
  return OPERATIONALLY_VISIBLE_REQUEST_STATUSES.has(status as GuestRequestStatus);
}

export interface ApiGuestRequest {
  id: string;
  stayId: string;
  roomNumber: string;
  guestName: string;
  category: GuestRequestCategory;
  description: string;
  status: GuestRequestStatus;
  claimedAt?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGuestRequestBody {
  stayId: string;
  roomNumber: string;
  guestName: string;
  category: GuestRequestCategory;
  description: string;
}

/**
 * Fetch all guest requests (staff view)
 */
export async function fetchGuestRequests(): Promise<ApiGuestRequest[]> {
  const response = await fetch(`${API_BASE}/requests`);
  if (!response.ok) {
    throw new Error(`Failed to fetch guest requests: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create a guest request (housekeeping, maintenance, concierge, etc.)
 */
export async function createGuestRequest(data: CreateGuestRequestBody): Promise<ApiGuestRequest> {
  const response = await fetch(`${API_BASE}/requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to create guest request: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update guest request status (staff action)
 */
export async function updateGuestRequestStatus(
  requestId: string,
  status: GuestRequestStatus
): Promise<ApiGuestRequest> {
  const response = await fetch(`${API_BASE}/requests/${requestId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update request status: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Cancel (delete) a guest request — guest-initiated
 */
export async function deleteGuestRequest(requestId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/requests/${requestId}`, {
    method: 'DELETE',
  });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to cancel request: ${response.statusText}`);
  }
}

/**
 * Fetch calendar events for current stay
 */
export async function fetchCalendarEvents(): Promise<ApiCalendarEvent[]> {
  const url = withRoomParam(`${API_BASE}/calendar`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch calendar: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch current bill
 */
export async function fetchBill(): Promise<ApiBill> {
  const url = withRoomParam(`${API_BASE}/bill`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch bill: ${response.statusText}`);
  }
  return response.json();
}

// =============================================================================
// CONCIERGE API (Phase 4A)
// =============================================================================

export enum ConciergeIntent {
  WHAT_SHOULD_I_DO_TODAY = 'WHAT_SHOULD_I_DO_TODAY',
  RECOMMENDATIONS = 'RECOMMENDATIONS',
  IM_HUNGRY = 'IM_HUNGRY',
  I_WANT_TO_RELAX = 'I_WANT_TO_RELAX',
  WHATS_HAPPENING_TONIGHT = 'WHATS_HAPPENING_TONIGHT',
}

export enum RecommendationType {
  VENUE = 'VENUE',
  SPA = 'SPA',
  ACTIVITY = 'ACTIVITY',
  LOCAL = 'LOCAL',
}

export enum RecommendationAction {
  BOOK = 'BOOK',
  VIEW = 'VIEW',
  EXPLORE = 'EXPLORE',
}

export interface Recommendation {
  id: string;
  type: RecommendationType;
  title: string;
  subtitle?: string;
  reason: string;
  image?: string;
  action: RecommendationAction;
  actionLabel?: string;
  resourceId?: string;
  price?: number;
  currency?: string;
}

export interface ConciergeResponse {
  intent: ConciergeIntent;
  spokenResponse: string;
  recommendations: Recommendation[];
  timestamp: string;
  context: {
    timeOfDay: string;
    dayOfWeek: number;
    weather?: string;
    guestName?: string;
  };
}

export interface ConciergeIntentInfo {
  id: ConciergeIntent;
  label: string;
  description: string;
}

/**
 * Guest context for contextual recommendations (Phase 4B)
 */
export interface GuestContextForApi {
  guestName?: string;
  stayDay?: number;
  isCheckoutDay?: boolean;
  isFirstDay?: boolean;
  todayBookingServiceIds?: string[];
  timeOfDay?: 'morning' | 'afternoon' | 'evening';
}

/**
 * Ask the concierge for recommendations
 */
export async function askConcierge(
  intent: ConciergeIntent,
  datetime?: Date,
  guestContext?: GuestContextForApi
): Promise<ConciergeResponse> {
  const response = await fetch(`${API_BASE}/concierge/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      intent,
      datetime: datetime?.toISOString(),
      ...guestContext,
    }),
  });
  if (!response.ok) {
    throw new Error(`Failed to ask concierge: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch available concierge intents
 */
export async function fetchConciergeIntents(): Promise<ConciergeIntentInfo[]> {
  const response = await fetch(`${API_BASE}/concierge/intents`);
  if (!response.ok) {
    throw new Error(`Failed to fetch intents: ${response.statusText}`);
  }
  return response.json();
}

// =============================================================================
// DEMO MODE API (Phase D4.5.5)
// =============================================================================

/**
 * Reset demo: Clear all bookings, reset calendar and bill
 */
export async function resetDemo(): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/demo/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error('Failed to reset demo');
  }
  return response.json();
}

/**
 * Clear all future bookings
 */
export async function clearAllBookings(): Promise<{ success: boolean; clearedCount: number }> {
  const response = await fetch(`${API_BASE}/demo/clear-bookings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error('Failed to clear bookings');
  }
  return response.json();
}

/**
 * Restart session: Reload hotel + stay state
 */
export async function restartSession(): Promise<{ success: boolean; shouldReload: boolean }> {
  const response = await fetch(`${API_BASE}/demo/restart-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error('Failed to restart session');
  }
  return response.json();
}

// =============================================================================
// HOUSEKEEPING API (Phase P2.5-1)
// =============================================================================

export type TaskOrigin = 'PMS_CHECKOUT' | 'MAINTENANCE' | 'GREEN_STAY' | 'GUEST_REQUEST' | 'DEFAULT';
export type TaskPriority = 'HIGH' | 'MEDIUM' | 'NORMAL' | 'LOW';
export type TaskStatus = 'PENDING' | 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'BLOCKED';
export type TaskType = 'FULL_CLEAN' | 'TOWELS_ONLY' | 'CHECKOUT_CLEAN' | 'SKIPPED' | 'MAINTENANCE';

export interface ApiHousekeepingTask {
  taskId: string;
  roomNumber: string;
  date: string;
  type: TaskType;
  status: TaskStatus;
  priority: TaskPriority;
  origin: TaskOrigin;
  assignedTo?: string;
  estimatedDuration: number;
  blocked: boolean;
  cancelReason?: string;
  createdFrom?: string;
  notes?: string;
  guestName?: string | null;
  stayId?: string | null;
  greenStayEnabled?: boolean;
}

/**
 * Generate housekeeping tasks for a specific date (PMS-driven)
 */
export async function generateHousekeepingTasks(
  hotelId: string,
  date: string
): Promise<{ tasks: ApiHousekeepingTask[]; summary: { total: number; highPriority: number; normal: number; blocked: number } }> {
  const response = await fetch(`${API_BASE}/housekeeping/generate-tasks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hotelId, date }),
  });
  if (!response.ok) {
    throw new Error('Failed to generate housekeeping tasks');
  }
  return response.json();
}

/**
 * Get all housekeeping tasks for a specific date
 */
export async function fetchHousekeepingTasks(
  date?: string
): Promise<{ date: string; tasks: ApiHousekeepingTask[] }> {
  const url = new URL(`${API_BASE}/housekeeping/tasks`);
  if (date) {
    url.searchParams.set('date', date);
  }
  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error('Failed to fetch housekeeping tasks');
  }
  return response.json();
}

/**
 * Handle PMS checkout event → generate HIGH priority cleaning task
 */
export async function handleCheckoutEvent(roomNumber: string): Promise<{ task: ApiHousekeepingTask }> {
  const response = await fetch(`${API_BASE}/housekeeping/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomNumber }),
  });
  if (!response.ok) {
    throw new Error('Failed to handle checkout event');
  }
  return response.json();
}

/**
 * Handle PMS check-in event → remove stale checkout tasks
 */
export async function handleCheckInEvent(roomNumber: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/housekeeping/checkin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomNumber }),
  });
  if (!response.ok) {
    throw new Error('Failed to handle check-in event');
  }
  return response.json();
}

/**
 * Handle room OUT_OF_SERVICE event → block all tasks
 */
export async function handleOutOfServiceEvent(roomNumber: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/housekeeping/out-of-service`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomNumber }),
  });
  if (!response.ok) {
    throw new Error('Failed to handle out-of-service event');
  }
  return response.json();
}

/**
 * Update housekeeping task status
 */
export async function updateTaskStatus(
  taskId: string,
  status: TaskStatus
): Promise<{ task: ApiHousekeepingTask }> {
  const response = await fetch(`${API_BASE}/housekeeping/tasks/${taskId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error('Failed to update task status');
  }
  return response.json();
}

// =============================================================================
// GDPR CONSENT API (PILOT-3)
// =============================================================================

/**
 * Record explicit guest GDPR consent in the activity ledger.
 * Fire-and-forget safe — consent is already stored in localStorage before this call.
 */
export async function recordConsent(
  stayId: string,
  roomNumber: string,
): Promise<{ success: boolean; id: string; consentTimestamp: string }> {
  const response = await fetch(`${API_BASE}/activity-ledger/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stayId, roomNumber, consentVersion: 'v1' }),
  });
  if (!response.ok) {
    throw new Error('Failed to record consent');
  }
  return response.json();
}

// =============================================================================
// API Object (for convenient imports)
// =============================================================================

export const api = {
  fetchHotel,
  fetchSession,
  fetchServices,
  fetchBookings,
  createBooking,
  cancelBooking,
  fetchCalendarEvents,
  fetchBill,
  askConcierge,
  fetchConciergeIntents,
  updateGreenPreferences,
  completeOnboarding,
  getRoomParam,
  // Guest requests
  createGuestRequest,
  // Housekeeping (Phase P2.5-1)
  generateHousekeepingTasks,
  fetchHousekeepingTasks,
  handleCheckoutEvent,
  handleCheckInEvent,
  handleOutOfServiceEvent,
  updateTaskStatus,
  // Demo mode
  resetDemo,
  clearAllBookings,
  restartSession,
  // GDPR consent (PILOT-3)
  recordConsent,
};
